import React, { useState, useMemo } from 'react'
import Modal from './Modal'
import { C, ui } from '../theme'

// Marching-formation editor. The army's men appear as draggable chips (one per pool slice);
// buckets are drop zones laid out as the marching column — Scouts on the left flank,
// Vanguard / Main body / Rear guard down the middle, Raiders + Supply-line guard on the
// right. Everyone starts in the Main body (guarding the carts).
//   drag chip → bucket   place men
//   shift-click chip     split it in half
//   ctrl-click chip      split off an exact number (inline input)
//   drop on same troops  slices of one pool recombine automatically per bucket
// Saved via POST /armies/:id/formation (only non-main-body counts are stored).

const BUCKET_META = {
  scouts: { label: 'Scouts', hint: 'feed scout parties (Scouting tab)' },
  vanguard: { label: 'Vanguard', hint: 'leads the column; meets a detected foe first' },
  main: { label: 'Main body', hint: 'guards the carts & carriages' },
  rear: { label: 'Rear guard', hint: 'buys time when cavalry comes for the carts' },
  raiders: { label: 'Raiders', hint: 'the men who raid (Orders tab)' },
  supply_line: { label: 'Supply-line guard', hint: 'ride the caravans coming to this army' },
}
const SAVED_BUCKETS = ['vanguard', 'rear', 'scouts', 'supply_line', 'raiders']
const SCOUT_TAGS = ['nimble', 'light_horse', 'flying', 'caster']

const chipsFromFormation = (army) => {
  const byPool = (army.formation && army.formation.byPool) || {}
  const chips = []
  let cid = 1
  for (const p of (army.pools || [])) {
    if (p.disbanded || !(p.men > 0)) continue
    const row = byPool[String(p.id)] || { main: p.men }
    for (const bucket of ['main', ...SAVED_BUCKETS]) {
      const men = row[bucket] || 0
      if (men > 0) chips.push({ cid: cid++, poolId: p.id, bucket, men, name: p.troop_name, veterancy: p.veterancy, tags: String(p.role_tags || '') })
    }
  }
  return chips
}

const assignmentsFromChips = (chips) => {
  const out = {}
  for (const ch of chips) {
    if (ch.bucket === 'main') continue
    const row = out[String(ch.poolId)] || {}
    row[ch.bucket] = (row[ch.bucket] || 0) + ch.men
    out[String(ch.poolId)] = row
  }
  return out
}

const scoutCapable = (tags) => SCOUT_TAGS.some(t => tags.split(',').map(s => s.trim()).includes(t))

export default function FormationModal({ army, onSave, onClose }) {
  const [chips, setChips] = useState(() => chipsFromFormation(army))
  const [dragCid, setDragCid] = useState(null)
  const [editCid, setEditCid] = useState(null)   // chip with the ctrl-click split input open
  const [editVal, setEditVal] = useState('')
  const [overBucket, setOverBucket] = useState(null)
  const [saving, setSaving] = useState(false)

  const initial = useMemo(() => JSON.stringify(assignmentsFromChips(chipsFromFormation(army))), [army])
  const dirty = JSON.stringify(assignmentsFromChips(chips)) !== initial

  // merge same-pool chips within a bucket (recombine-on-drop) and drop empties
  const normalize = (list) => {
    const seen = {}
    const out = []
    for (const ch of list) {
      if (!(ch.men > 0)) continue
      const key = `${ch.poolId}:${ch.bucket}`
      if (seen[key] != null) out[seen[key]] = { ...out[seen[key]], men: out[seen[key]].men + ch.men }
      else { seen[key] = out.length; out.push(ch) }
    }
    return out
  }

  const moveChip = (cid, bucket) => {
    setChips(cs => normalize(cs.map(ch => ch.cid === cid ? { ...ch, bucket } : ch)))
    setEditCid(null)
  }
  const splitChip = (ch, off) => {
    off = Math.max(1, Math.min(ch.men - 1, Math.floor(off)))
    if (!(ch.men > 1)) return
    setChips(cs => {
      const nextCid = cs.reduce((m, c) => Math.max(m, c.cid), 0) + 1
      return cs.flatMap(c => c.cid === ch.cid
        ? [{ ...c, men: c.men - off }, { ...c, cid: nextCid, men: off }]
        : [c])
    })
  }
  const chipClick = (e, ch) => {
    if (e.shiftKey) { splitChip(ch, Math.floor(ch.men / 2)); return }
    if (e.ctrlKey || e.metaKey) { setEditCid(ch.cid); setEditVal(String(Math.floor(ch.men / 2))); return }
  }
  const commitEdit = (ch) => {
    const n = Math.floor(Number(editVal))
    setEditCid(null)
    if (n >= 1 && n <= ch.men - 1) splitChip(ch, n)
  }

  const totals = useMemo(() => {
    const t = { main: 0 }
    for (const b of SAVED_BUCKETS) t[b] = 0
    for (const ch of chips) t[ch.bucket] += ch.men
    return t
  }, [chips])

  const save = async () => {
    setSaving(true)
    const res = await onSave(assignmentsFromChips(chips))
    setSaving(false)
    if (res && res.ok) onClose()
  }

  const chip = (ch) => (
    <div key={ch.cid} draggable
      onDragStart={(e) => { setDragCid(ch.cid); e.dataTransfer.effectAllowed = 'move' }}
      onDragEnd={() => { setDragCid(null); setOverBucket(null) }}
      onClick={(e) => chipClick(e, ch)}
      title={`${ch.name} — shift-click: split in half · ctrl-click: split an exact number · drag to a bucket`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', margin: 2,
        borderRadius: 12, cursor: 'grab', fontSize: 12, userSelect: 'none',
        border: `1px solid ${C.ruleStrong}`, background: C.paperRaised, color: C.ink,
        opacity: dragCid === ch.cid ? 0.4 : 1,
      }}>
      <b>{ch.men}</b>
      <span style={{ whiteSpace: 'nowrap' }}>{ch.name}</span>
      {scoutCapable(ch.tags) && <span title="scout-capable" style={{ fontSize: 10, color: C.green }}>◉</span>}
      {editCid === ch.cid && (
        <input autoFocus type="number" min={1} max={ch.men - 1} value={editVal}
          onChange={e => setEditVal(e.target.value)}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => { if (e.key === 'Enter') commitEdit(ch); if (e.key === 'Escape') setEditCid(null) }}
          onBlur={() => commitEdit(ch)}
          style={{ width: 52, padding: '1px 4px', fontSize: 12, border: `1px solid ${C.gold}`, borderRadius: 4, background: C.paperSunken, color: C.ink }} />
      )}
    </div>
  )

  const bucketBox = (key, { minHeight = 64, flex } = {}) => {
    const meta = BUCKET_META[key]
    const inBucket = chips.filter(ch => ch.bucket === key)
    const isOver = overBucket === key
    return (
      <div key={key}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overBucket !== key) setOverBucket(key) }}
        onDragLeave={() => { if (overBucket === key) setOverBucket(null) }}
        onDrop={(e) => { e.preventDefault(); if (dragCid != null) moveChip(dragCid, key); setDragCid(null); setOverBucket(null) }}
        style={{
          flex, minHeight, padding: 6, borderRadius: 6, marginBottom: 6,
          border: `1.5px ${isOver ? 'solid' : 'dashed'} ${isOver ? C.gold : C.rule}`,
          background: isOver ? C.highlight : C.paperSunken,
        }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 2 }}>
          <b style={{ fontFamily: "'Cinzel', serif", fontSize: 12, letterSpacing: 0.5 }}>{meta.label}</b>
          <span style={{ fontSize: 11, opacity: 0.7 }}>{totals[key] || 0} men</span>
        </div>
        <div style={{ fontSize: 10, opacity: 0.55, marginBottom: 3 }}>{meta.hint}</div>
        <div>{inBucket.map(chip)}</div>
      </div>
    )
  }

  return (
    <Modal title={`Formation — ${army.name}`} onClose={onClose} width={760}>
      <div style={{ fontSize: 11.5, opacity: 0.7, margin: '2px 0 10px' }}>
        Drag troops into a bucket. <b>Shift-click</b> splits a group in half; <b>Ctrl-click</b> splits off an exact
        number; slices of the same troops recombine when they land in the same bucket. Unassigned men march with
        the main body, guarding the carts.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        {/* left flank — scouts */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {bucketBox('scouts', { minHeight: 120, flex: 1 })}
        </div>
        {/* the column — vanguard / main body / rear guard */}
        <div style={{ flex: 1.6, display: 'flex', flexDirection: 'column' }}>
          {bucketBox('vanguard')}
          {bucketBox('main', { minHeight: 130, flex: 1 })}
          {bucketBox('rear')}
        </div>
        {/* right flank — raiders & caravan guards */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {bucketBox('raiders', { minHeight: 90 })}
          {bucketBox('supply_line', { minHeight: 90, flex: 1 })}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 10 }}>
        <span style={{ fontSize: 11, opacity: 0.6, flex: 1 }}>
          ◉ = scout-capable · {chips.reduce((s, c) => s + c.men, 0)} men total
        </span>
        <button style={ui.btnGhost} onClick={() => setChips(chipsFromFormation({ ...army, formation: null }))}>
          Reset — all to main body
        </button>
        <button style={{ ...ui.btn, opacity: dirty && !saving ? 1 : 0.55 }} disabled={!dirty || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save formation'}
        </button>
      </div>
    </Modal>
  )
}
